/**
 * Shared checklist template types for API contracts between frontend and backend
 */
import { z } from "zod";
import { nameSchema, uuidSchema, IdParamSchema, CompanyIdParamSchema, } from "./validation-common.js";
// ===== ZOD VALIDATION SCHEMAS =====
/**
 * Schema for item description (non-empty string, max 500 chars)
 */
const itemDescriptionSchema = z.string().trim().min(1).max(500);
/**
 * Schema for items array (non-empty array of strings)
 */
const itemsSchema = z.array(itemDescriptionSchema).min(1);
/**
 * Schema for creating a checklist template
 * Input: User provides item descriptions (strings)
 * Backend stores descriptions directly in the items JSON field
 */
export const CreateChecklistTemplateInputSchema = z.object({
    name: nameSchema,
    items: itemsSchema,
    jobRoleIds: z.array(uuidSchema).optional(),
    jobRoleFamilyIds: z.array(uuidSchema).optional(),
    locationId: uuidSchema.optional(),
});
/**
 * Schema for route-level validation (includes companyId)
 * This is used by express-zod-safe middleware in routes
 */
export const CreateChecklistTemplateBodySchema = z.object({
    name: nameSchema,
    items: itemsSchema,
    jobRoleIds: z.array(uuidSchema).optional(),
    jobRoleFamilyIds: z.array(uuidSchema).optional(),
    locationId: uuidSchema.optional(),
    companyId: uuidSchema,
});
/**
 * Schema for updating a checklist template
 */
export const UpdateChecklistTemplateInputSchema = z.object({
    name: nameSchema.optional(),
    items: itemsSchema.optional(),
    jobRoleIds: z.array(uuidSchema).optional(),
    jobRoleFamilyIds: z.array(uuidSchema).optional(),
    locationId: uuidSchema.optional(),
});
// ===== ROUTE PARAMETER SCHEMAS =====
// Re-export common parameter schemas from validation-common
export { IdParamSchema, CompanyIdParamSchema };
// Alias for backwards compatibility (IdParamSchema is preferred)
export const ChecklistTemplateIdParamSchema = IdParamSchema;
/**
 * Schema for query parameters when listing checklist templates
 */
export const ChecklistTemplateQuerySchema = z.object({
    jobRoleIds: z.array(uuidSchema).optional(),
    jobRoleFamilyIds: z.array(uuidSchema).optional(),
    locationId: uuidSchema.optional(),
});
// ===== RESPONSE SCHEMAS =====
export const ChecklistTemplateResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    items: z.array(z.string()),
    companyId: z.uuid(),
    jobRoleIds: z.array(z.uuid()),
    jobRoleFamilyIds: z.array(z.uuid()),
    locationId: z.uuid().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
